/**
 * User-preference store.
 *
 * Persists theme / tenant / locale. Today this is localStorage-backed; the
 * interface is deliberately async-shaped so a server-backed Settings API can
 * drop in later without touching callers (the driver just becomes a fetch).
 */
import {
  DEFAULT_THEME,
  DEFAULT_TENANT,
  isTheme,
  isTenant,
  type Theme,
  type Tenant,
} from "@oxbo/tokens";
import { DEFAULT_LOCALE, isLocale, type Locale } from "@i18n/locales";
import { isPaperSize, type PaperSize } from "@lib/paperSize";

export interface Preferences {
  theme: Theme;
  tenant: Tenant;
  locale: Locale;
  /** Left-nav collapsed (icon-only rail) vs expanded (icon + label). */
  navCollapsed: boolean;
  /**
   * The user's chosen unit-system display profile key (e.g. `api` / `metric` /
   * a company profile). `null` = Auto — follow the org default, falling back to
   * each record's own stored profile. This never moves stored values; it only
   * chooses the units every measurement is *shown* in.
   */
  displayProfile: string | null;
  /** Optional display-currency override (ISO code). `null` = show native currency. */
  displayCurrency: string | null;
  /** Report page size, or `null` = Auto (company default). */
  paperSize: PaperSize | null;
}

const KEYS = {
  theme: "oxbo.theme",
  tenant: "oxbo.tenant",
  locale: "oxbo.locale",
  navCollapsed: "oxbo.navCollapsed",
  displayProfile: "oxbo.displayProfile",
  displayCurrency: "oxbo.displayCurrency",
  paperSize: "oxbo.paperSize",
} as const;

/** Sentinel stored for the "Auto / follow default" choice. */
const AUTO = "auto";

function safeGet(key: string): string | null {
  try {
    return typeof localStorage !== "undefined" ? localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(key, value);
  } catch {
    /* storage disabled (private mode / quota) — degrade to in-memory only */
  }
}

export function loadPreferences(): Preferences {
  const theme = safeGet(KEYS.theme);
  const tenant = safeGet(KEYS.tenant);
  const locale = safeGet(KEYS.locale);
  const navCollapsed = safeGet(KEYS.navCollapsed);
  const displayProfile = safeGet(KEYS.displayProfile);
  const displayCurrency = safeGet(KEYS.displayCurrency);
  const paperSize = safeGet(KEYS.paperSize);
  return {
    theme: isTheme(theme) ? theme : DEFAULT_THEME,
    tenant: isTenant(tenant) ? tenant : DEFAULT_TENANT,
    locale: isLocale(locale) ? locale : DEFAULT_LOCALE,
    // Default expanded; only an explicit "true" collapses the rail.
    navCollapsed: navCollapsed === "true",
    // Default (unset or the AUTO sentinel) = Auto: follow the org/record default.
    displayProfile: displayProfile && displayProfile !== AUTO ? displayProfile : null,
    displayCurrency: displayCurrency && displayCurrency !== AUTO ? displayCurrency : null,
    paperSize: isPaperSize(paperSize) ? paperSize : null,
  };
}

export function saveTheme(theme: Theme): void {
  safeSet(KEYS.theme, theme);
}
export function saveTenant(tenant: Tenant): void {
  safeSet(KEYS.tenant, tenant);
}
export function saveLocale(locale: Locale): void {
  safeSet(KEYS.locale, locale);
}
export function saveNavCollapsed(collapsed: boolean): void {
  safeSet(KEYS.navCollapsed, collapsed ? "true" : "false");
}
export function saveDisplayProfile(profile: string | null): void {
  safeSet(KEYS.displayProfile, profile ?? AUTO);
}
export function saveDisplayCurrency(currency: string | null): void {
  safeSet(KEYS.displayCurrency, currency ?? AUTO);
}
export function savePaperSize(paper: PaperSize | null): void {
  safeSet(KEYS.paperSize, paper ?? AUTO);
}
